/* iter55p14 — Public author deep-link page. Bio + optional sample
 * chapter reader + optional contact form. Fully unauthenticated. */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Mail, Send, Ticket, User } from 'lucide-react';
import { P, FONTS } from '../components/landing/scriptorium/palette';
import StructuredData, { authorProfileLd, breadcrumbLd } from '@/components/seo/StructuredData';
import { useSeoMeta } from '@/lib/useSeoMeta';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function Portrait({ url, size = 88 }) {
  const resolved = url && url.startsWith('/api/')
    ? ((process.env.REACT_APP_BACKEND_URL || '') + url)
    : url;
  return (
    <div className="shrink-0 overflow-hidden rounded-full border"
         style={{ width: size, height: size, borderColor: P.gold }}>
      {resolved
        ? <img src={resolved} alt="" className="h-full w-full object-cover" />
        : <User className="h-full w-full p-4 text-muted-foreground" />}
    </div>
  );
}

function ContactForm({ pen_slug, pen_name }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 8) { toast.error('Message must be at least 8 characters.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/authors/${pen_slug}/contact`, {
        from_name: name.trim(), from_email: email.trim(), body: body.trim(),
      });
      toast.success(`Sent to ${pen_name}.`);
      setName(''); setEmail(''); setBody('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send. Try again.');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="author-contact-form" className="mt-8 rounded-lg border p-4"
          style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep }}>
      <div className="mb-3 flex items-center gap-2 text-[13px] font-semibold"
           style={{ color: P.ink, fontFamily: FONTS.display }}>
        <Mail className="h-4 w-4" style={{ color: P.oxblood }} /> Write to {pen_name}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <input required minLength={1} maxLength={80} placeholder="Your name"
               value={name} onChange={(e) => setName(e.target.value)}
               data-testid="author-contact-name"
               className="rounded-sm border px-2 py-1.5 text-sm"
               style={{ borderColor: `${P.gold}55`, backgroundColor: P.paper }} />
        <input required type="email" placeholder="Your email"
               value={email} onChange={(e) => setEmail(e.target.value)}
               data-testid="author-contact-email"
               className="rounded-sm border px-2 py-1.5 text-sm"
               style={{ borderColor: `${P.gold}55`, backgroundColor: P.paper }} />
      </div>
      <textarea required rows={5} minLength={8} maxLength={2000}
                placeholder={`Anything to say to ${pen_name}? Kind words welcomed.`}
                value={body} onChange={(e) => setBody(e.target.value)}
                data-testid="author-contact-body"
                className="mt-2 w-full rounded-sm border px-2 py-1.5 text-sm"
                style={{ borderColor: `${P.gold}55`, backgroundColor: P.paper }} />
      <div className="mt-2 flex items-center justify-between text-[10.5px]"
           style={{ color: P.inkSoft, fontFamily: FONTS.type }}>
        <span>{body.length}/2000 · your email is never public</span>
        <button type="submit" disabled={busy}
                data-testid="author-contact-send"
                className="inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-[12px] font-semibold text-white transition disabled:opacity-60"
                style={{ backgroundColor: P.oxblood }}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Send message
        </button>
      </div>
    </form>
  );
}

export default function AuthorPage() {
  const { penSlug } = useParams();
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState('');
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/authors/${penSlug}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || 'Author not found.'); });
    return () => { alive = false; };
  }, [penSlug]);

  // 2026-10 · SEO audit fix — this page never set a <title>/description
  // at all before, so it just inherited whatever the previous route
  // left behind. Fallback covers the loading/error states too.
  useSeoMeta(`/authors/${penSlug}`, {
    title: data?.pen_name ? `${data.pen_name} — Novels & Books | Crown & Rose` : `Author — Crown & Rose`,
    description: data?.bio_short || `Novels published direct-to-reader through Crown & Rose.`,
  });

  if (err) {
    return (
      <div className="min-h-screen px-6 py-12" style={{ backgroundColor: P.paper, color: P.ink }}>
        <Link to="/" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: P.oxblood }}>
          <ArrowLeft className="h-3.5 w-3.5" /> Back to the front page
        </Link>
        <div className="mt-8 text-center text-lg" style={{ fontFamily: FONTS.display }}>{err}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: P.paper, color: P.inkSoft }}>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading author…
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: P.paper, color: P.ink }}
         data-testid="author-page">
      {/* iter55g69 — Rich-result ProfilePage schema for search engines. */}
      <StructuredData
        id={`author-${penSlug}`}
        data={authorProfileLd({
          siteUrl:     typeof window !== 'undefined' ? window.location.origin : '',
          penName:     data.pen_name,
          penSlug,
          bio:         data.bio_long || data.bio_short || '',
          portraitUrl: data.portrait_url || '',
          socialUrls:  (data.social_links || []).map((l) => l.url).filter(Boolean),
        })}
      />
      <StructuredData
        id="breadcrumb"
        data={breadcrumbLd([
          { name: 'Crown & Rose', url: typeof window !== 'undefined' ? window.location.origin : '' },
          { name: data.pen_name || 'Author', url: typeof window !== 'undefined' ? `${window.location.origin}/authors/${penSlug}` : '' },
        ])}
      />
      <header className="mx-auto max-w-3xl px-6 pt-10">
        <Link to="/" className="inline-flex items-center gap-1 text-[12px] hover:underline"
              style={{ color: P.oxblood, fontFamily: FONTS.type }}>
          <ArrowLeft className="h-3 w-3" /> Back to Top Writers
        </Link>
      </header>
      <section className="mx-auto max-w-3xl px-6 py-8">
        <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
          <Portrait url={data.portrait_url} size={96} />
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.32em]"
                 style={{ color: P.oxblood, fontFamily: FONTS.type }}>
              An author on The Loom
            </div>
            <h1 className="mt-1 truncate text-3xl" style={{ fontFamily: FONTS.display, fontWeight: 600 }}>
              {data.pen_name}
            </h1>
            {data.bio_short && (
              <p className="mt-2 text-[15px] leading-relaxed" style={{ color: P.inkSoft }}>
                {data.bio_short}
              </p>
            )}
            {data.social_links?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="author-page-social-links">
                {data.social_links.map((link, idx) => (
                  <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer"
                     data-testid={`author-page-social-${idx}`}
                     className="rounded-sm border px-2.5 py-1 text-[11.5px] font-medium hover:opacity-80"
                     style={{ borderColor: `${P.gold}55`, color: P.oxblood, fontFamily: FONTS.type }}>
                    {link.label}
                  </a>
                ))}
              </div>
            )}
            {data.stats_opt_in && (
              <div className="mt-3 flex items-center gap-4 text-[12px]"
                   style={{ color: P.inkSoft, fontFamily: FONTS.type }}
                   data-testid="author-page-stats">
                <span>
                  <span className="text-lg font-semibold" style={{ color: P.oxblood, fontFamily: FONTS.display }}>
                    {data.books_sold ?? 0}
                  </span>{' '}books sold
                </span>
                <span aria-hidden="true" style={{ color: `${P.inkSoft}55` }}>·</span>
                <span>
                  <span className="text-lg font-semibold" style={{ color: P.oxblood, fontFamily: FONTS.display }}>
                    {data.books_in_pipeline ?? 0}
                  </span>{' '}in the pipeline
                </span>
              </div>
            )}
          </div>
        </div>

        {data.bio_long && (
          <div className="mt-8" data-testid="author-page-bio-long">
            <h2 className="text-lg" style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}>
              About {data.pen_name}
            </h2>
            <p className="mt-2 whitespace-pre-wrap text-[14.5px] leading-relaxed" style={{ color: P.inkSoft }}>
              {data.bio_long}
            </p>
          </div>
        )}

        {data.sample_opt_in && (
          <div className="mt-8 rounded-lg border p-5" data-testid="author-page-sample"
               style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep }}>
            <div className="mb-2 inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10.5px] font-semibold"
                 style={{ backgroundColor: `${P.gold}44`, color: P.leather, fontFamily: FONTS.type }}>
              <Ticket className="h-3 w-3" /> Free sample · {data.sample_label || 'Sample chapter'}
            </div>
            <h2 className="mt-2 text-xl" style={{ fontFamily: FONTS.display, fontWeight: 600 }}>
              {data.sample?.title || data.sample_label || 'Sample chapter'}
            </h2>
            {data.sample?.word_count > 0 && (
              <div className="text-[10.5px]" style={{ color: P.inkSoft, fontFamily: FONTS.type }}>
                {data.sample.word_count.toLocaleString()} words
              </div>
            )}
            {data.sample?.body ? (
              <>
                <article className="prose prose-sm mt-4 max-w-none whitespace-pre-wrap text-[14px] leading-relaxed"
                          style={{ color: P.ink }}>
                  {data.sample.body}
                </article>
                {data.sample.truncated && (
                  <div className="mt-4 flex flex-col items-start gap-2 rounded-sm border-l-4 p-3 sm:flex-row sm:items-center sm:justify-between"
                        data-testid="author-page-sample-upsell"
                        style={{ borderColor: P.oxblood, backgroundColor: `${P.gold}22` }}>
                    <div className="text-[12.5px]" style={{ color: P.ink }}>
                      Showing the first <strong>{(data.sample.displayed_words || 0).toLocaleString()}</strong> of
                      &nbsp;<strong>{(data.sample.word_count || 0).toLocaleString()}</strong> words.
                      {data.sample.bookstore_url
                        ? ' Read the rest on the Crown & Rose bookstore.'
                        : ' The full chapter is only on the bookstore.'}
                    </div>
                    {data.sample.bookstore_url ? (
                      <a href={data.sample.bookstore_url}
                          data-testid="author-page-sample-buy"
                          className="inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-[12px] font-semibold text-white"
                          style={{ backgroundColor: P.oxblood }}>
                        Buy on the Bookstore
                      </a>
                    ) : (
                      <a href="/bookstore"
                          data-testid="author-page-sample-visit-store"
                          className="inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-[12px] font-semibold text-white"
                          style={{ backgroundColor: P.oxblood }}>
                        Visit the bookstore
                      </a>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="mt-3 text-[13px] italic" style={{ color: P.inkSoft }}>
                {data.pen_name} hasn&apos;t published a preview yet — check back soon.
              </p>
            )}
          </div>
        )}

        {data.contact_open && <ContactForm pen_slug={data.pen_slug} pen_name={data.pen_name} />}
      </section>
    </div>
  );
}
